import { Controller } from "@hotwired/stimulus"

let googleMapsLoaderPromise = null

function loadGoogleMaps (apiKey) {
  if (window.google?.maps?.Map) {
    return Promise.resolve()
  }

  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const callbackName = "__waveGoogleMapsMinimalReady"
    window[callbackName] = () => {
      delete window[callbackName]
      resolve()
    }

    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&language=es&region=CL&callback=${callbackName}&loading=async`
    script.async = true
    script.defer = true
    script.onerror = () => {
      googleMapsLoaderPromise = null
      reject(new Error("Google Maps failed to load"))
    }
    document.head.appendChild(script)
  })

  return googleMapsLoaderPromise
}

// Airbnb-like muted roadmap (desaturated, few POIs/labels).
const MINIMAL_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e5e5" }, { visibility: "on" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] }
]

export default class extends Controller {
  static targets = ["canvas"]

  static values = {
    apiKey: String,
    latitude: Number,
    longitude: Number,
    query: String,
    zoom: { type: Number, default: 15 }
  }

  connect () {
    if (!this.apiKeyValue || !this.hasCanvasTarget) return

    loadGoogleMaps(this.apiKeyValue)
      .then(() => this.resolveCenter())
      .then((center) => {
        if (!center) return
        this.renderMap(center)
      })
      .catch((error) => console.error(error))
  }

  disconnect () {
    this.map = null
    this.marker = null
  }

  resolveCenter () {
    if (Number.isFinite(this.latitudeValue) && Number.isFinite(this.longitudeValue)) {
      return Promise.resolve({ lat: this.latitudeValue, lng: this.longitudeValue })
    }

    const query = this.hasQueryValue ? this.queryValue.trim() : ""
    if (!query || !window.google?.maps?.Geocoder) return Promise.resolve(null)

    const geocoder = new google.maps.Geocoder()
    return new Promise((resolve) => {
      geocoder.geocode({ address: query, region: "CL" }, (results, status) => {
        if (status !== "OK" || !results?.[0]?.geometry?.location) {
          resolve(null)
          return
        }

        const location = results[0].geometry.location
        resolve({ lat: location.lat(), lng: location.lng() })
      })
    })
  }

  renderMap (center) {
    this.map = new google.maps.Map(this.canvasTarget, {
      center,
      zoom: this.zoomValue,
      disableDefaultUI: true,
      gestureHandling: "cooperative",
      clickableIcons: false,
      styles: MINIMAL_MAP_STYLES,
      backgroundColor: "#f5f5f5"
    })

    this.marker = new google.maps.Marker({
      position: center,
      map: this.map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#111111",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 3
      }
    })
  }
}
